import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, User, CalendarOff, X, ChevronLeft, ChevronRight } from "lucide-react";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addMonths, subMonths, getDaysInMonth, getDay } from "date-fns";

const WEEKDAYS = [
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
  { label: "Sun", value: 7 },
];

const DAY_LABELS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

function AvailWeekCalendar({ month, onMonthChange, onWeekClick, selectedWeekStart }) {
  const year = month.getFullYear();
  const mon = month.getMonth();
  const daysInMonth = getDaysInMonth(month);
  const firstDow = (getDay(new Date(year, mon, 1)) + 6) % 7;
  const today = format(new Date(), "yyyy-MM-dd");

  const isInSelectedWeek = (dateStr) => {
    if (!selectedWeekStart) return false;
    const end = new Date(selectedWeekStart + "T00:00:00");
    end.setDate(end.getDate() + 6);
    return dateStr >= selectedWeekStart && dateStr <= format(end, "yyyy-MM-dd");
  };

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 p-3 select-none">
      <div className="flex items-center justify-between mb-3 px-1">
        <button type="button" onClick={() => onMonthChange(subMonths(month, 1))} className="text-slate-400 hover:text-white p-1 rounded">
          <ChevronLeft size={15} />
        </button>
        <span className="text-sm text-white font-medium">{format(month, "MMMM yyyy")}</span>
        <button type="button" onClick={() => onMonthChange(addMonths(month, 1))} className="text-slate-400 hover:text-white p-1 rounded">
          <ChevronRight size={15} />
        </button>
      </div>
      <div className="grid grid-cols-7 mb-1">
        {DAY_LABELS.map((l) => <div key={l} className="text-center text-[11px] text-slate-500 py-1">{l}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-y-1">
        {cells.map((d, i) => {
          if (!d) return <div key={`e-${i}`} />;
          const dateStr = `${year}-${String(mon + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const inWeek = isInSelectedWeek(dateStr);
          const isToday = dateStr === today;
          return (
            <button key={dateStr} type="button" tabIndex={-1}
              onClick={() => onWeekClick(new Date(year, mon, d))}
              className={`h-7 w-7 mx-auto rounded-full text-xs font-medium transition-colors flex items-center justify-center focus:outline-none
                ${inWeek ? "bg-emerald-500/30 text-emerald-300 hover:bg-emerald-500/50"
                  : isToday ? "text-indigo-400 font-semibold hover:bg-slate-700"
                  : "text-slate-300 hover:bg-slate-700 hover:text-white"}`}>
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MiniCalendar({ month, onMonthChange, onDayClick, mode, periods }) {
  const year = month.getFullYear();
  const mon = month.getMonth();
  const daysInMonth = getDaysInMonth(month);
  // Monday-based: getDay returns 0=Sun,1=Mon..6=Sat → shift so Mon=0
  const firstDow = (getDay(new Date(year, mon, 1)) + 6) % 7;

  const isBlocked = (dateStr) => periods.some((p) => {
    if (typeof p === "string") return p === dateStr;
    if (p.date) return p.date === dateStr;
    if (p.from && p.to) return dateStr >= p.from && dateStr <= p.to;
    return false;
  });

  const isPartial = (dateStr) => periods.some((p) => {
    const matches = (typeof p === "string" && p === dateStr) ||
      (p.date && p.date === dateStr) ||
      (p.from && p.to && dateStr >= p.from && dateStr <= p.to);
    return matches && p.time_from;
  });

  const today = format(new Date(), "yyyy-MM-dd");
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 p-3 select-none">
      <div className="flex items-center justify-between mb-3 px-1">
        <button type="button" onClick={() => onMonthChange(subMonths(month, 1))} className="text-slate-400 hover:text-white p-1 rounded">
          <ChevronLeft size={15} />
        </button>
        <span className="text-sm text-white font-medium">{format(month, "MMMM yyyy")}</span>
        <button type="button" onClick={() => onMonthChange(addMonths(month, 1))} className="text-slate-400 hover:text-white p-1 rounded">
          <ChevronRight size={15} />
        </button>
      </div>
      <div className="grid grid-cols-7 mb-1">
        {DAY_LABELS.map((l) => (
          <div key={l} className="text-center text-[11px] text-slate-500 py-1">{l}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-1">
        {cells.map((d, i) => {
          if (!d) return <div key={`e-${i}`} />;
          const dateStr = `${year}-${String(mon + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const blocked = isBlocked(dateStr);
          const partial = isPartial(dateStr);
          const isToday = dateStr === today;
          return (
            <button
              key={dateStr}
              type="button"
              tabIndex={-1}
              onClick={() => onDayClick(new Date(year, mon, d))}
              className={`h-7 w-7 mx-auto rounded-full text-xs font-medium transition-colors flex items-center justify-center focus:outline-none
                ${blocked || partial
                  ? "bg-amber-500/30 text-amber-300 hover:bg-amber-500/50"
                  : isToday
                  ? "text-indigo-400 font-semibold hover:bg-slate-700"
                  : "text-slate-300 hover:bg-slate-700 hover:text-white"}`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const emptyStaff = { name: "", email: "", is_active: true, service_ids: [] };

const TIME_HOURS = Array.from({ length: 17 }, (_, i) => String(i + 7).padStart(2, "0")); // 07–23
const TIME_MINUTES = ["00", "15", "30", "45"];
const SELECT_CLS = "bg-slate-800 border border-slate-700 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-500";

function TimeSelect({ value, onChange }) {
  const [h, m] = value.split(":");
  return (
    <div className="flex items-center gap-1">
      <select value={h} onChange={(e) => onChange(`${e.target.value}:${m}`)} className={SELECT_CLS}>
        {TIME_HOURS.map((hr) => <option key={hr} value={hr}>{hr}</option>)}
      </select>
      <span className="text-slate-500 text-xs">:</span>
      <select value={m} onChange={(e) => onChange(`${h}:${e.target.value}`)} className={SELECT_CLS}>
        {TIME_MINUTES.map((mn) => <option key={mn} value={mn}>{mn}</option>)}
      </select>
    </div>
  );
}

function BlockedPeriodsEditor({ periods, onChange }) {
  const [mode, setMode] = useState("day"); // day | week | month
  const [calMonth, setCalMonth] = useState(new Date());
  const [wholeDay, setWholeDay] = useState(true);
  const [timeFrom, setTimeFrom] = useState("09:00");
  const [timeTo, setTimeTo] = useState("17:00");
  // Weekly recurring time range
  const [weeklyWholeDay, setWeeklyWholeDay] = useState(true);
  const [weeklyTimeFrom, setWeeklyTimeFrom] = useState("09:00");
  const [weeklyTimeTo, setWeeklyTimeTo] = useState("17:00");
  // Weekly availability override
  const [availCalMonth, setAvailCalMonth] = useState(new Date());
  const [availWeekStart, setAvailWeekStart] = useState(null);
  const [availDaySlots, setAvailDaySlots] = useState([]);

  const addPeriod = (period) => {
    const already = periods.some((p) => JSON.stringify(p) === JSON.stringify(period));
    if (!already) onChange([...periods, period]);
  };

  const removePeriod = (idx) => onChange(periods.filter((_, i) => i !== idx));

  const toggleWeekday = (wd) => {
    const existing = periods.find((p) => p.weekdays);
    if (existing) {
      const wds = existing.weekdays.includes(wd)
        ? existing.weekdays.filter((d) => d !== wd)
        : [...existing.weekdays, wd].sort();
      const updated = wds.length === 0
        ? periods.filter((p) => !p.weekdays)
        : periods.map((p) => (p.weekdays ? { weekdays: wds, ...(weeklyWholeDay ? {} : { time_from: weeklyTimeFrom, time_to: weeklyTimeTo }) } : p));
      onChange(updated);
    } else {
      const base = { weekdays: [wd] };
      if (!weeklyWholeDay) { base.time_from = weeklyTimeFrom; base.time_to = weeklyTimeTo; }
      onChange([...periods, base]);
    }
  };

  // Sync weekly time prefs onto existing weekday entry when they change
  const updateWeeklyTimes = (whDay, tfrom, tto) => {
    setWeeklyWholeDay(whDay);
    setWeeklyTimeFrom(tfrom);
    setWeeklyTimeTo(tto);
    const existing = periods.find((p) => p.weekdays);
    if (!existing) return;
    const updated = periods.map((p) =>
      p.weekdays ? { weekdays: p.weekdays, ...(whDay ? {} : { time_from: tfrom, time_to: tto }) } : p
    );
    onChange(updated);
  };

  const activeWeekdays = periods.find((p) => p.weekdays)?.weekdays || [];

  const handleDayClick = (day) => {
    if (!day) return;
    const dateStr = format(day, "yyyy-MM-dd");
    const timeProps = wholeDay ? {} : { time_from: timeFrom, time_to: timeTo };
    if (mode === "day") {
      const alreadyBlocked = periods.some((p) =>
        (typeof p === "string" && p === dateStr) || (p.date && p.date === dateStr)
      );
      if (alreadyBlocked) {
        // Toggle off — remove the existing block for this date
        onChange(periods.filter((p) => {
          if (typeof p === "string") return p !== dateStr;
          if (p.date) return p.date !== dateStr;
          return true;
        }));
        return;
      }
      const newPeriod = wholeDay ? dateStr : { date: dateStr, ...timeProps };
      onChange([...periods, newPeriod]);
    } else if (mode === "week") {
      const from = format(startOfWeek(day, { weekStartsOn: 1 }), "yyyy-MM-dd");
      const to = format(endOfWeek(day, { weekStartsOn: 1 }), "yyyy-MM-dd");
      addPeriod({ from, to, ...timeProps });
    } else if (mode === "month") {
      const from = format(startOfMonth(day), "yyyy-MM-dd");
      const to = format(endOfMonth(day), "yyyy-MM-dd");
      addPeriod({ from, to, ...timeProps });
    }
  };

  const timeLabel = (p) => p.time_from ? ` · ${p.time_from}–${p.time_to}` : "";

  const labelFor = (p) => {
    if (typeof p === "string") return p;
    if (p.weekdays) return `Every ${p.weekdays.map((d) => WEEKDAYS.find((w) => w.value === d)?.label).join(", ")}${timeLabel(p)}`;
    if (p.date) return `${p.date}${timeLabel(p)}`;
    return `${p.from} → ${p.to}${timeLabel(p)}`;
  };

  return (
    <div className="space-y-4">
      {/* Weekly off days */}
      <div>
        <Label className="text-slate-300 text-sm mb-2 block">Weekly recurring off days</Label>
        <div className="flex flex-wrap gap-2 mb-2">
          {WEEKDAYS.map((w) => (
            <button
              key={w.value}
              type="button"
              onClick={() => toggleWeekday(w.value)}
              className={`px-3 py-1 rounded-lg text-xs font-medium border transition-colors ${
                activeWeekdays.includes(w.value)
                  ? "bg-red-500/20 border-red-500/50 text-red-300"
                  : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500"
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
        {activeWeekdays.length > 0 && (
          <div className="mt-2 space-y-2">
            <div className="flex gap-2">
              <button type="button" onClick={() => updateWeeklyTimes(true, weeklyTimeFrom, weeklyTimeTo)}
                className={`px-3 py-1 rounded-lg text-xs border transition-colors ${weeklyWholeDay ? "bg-red-500/20 border-red-500/40 text-red-300" : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"}`}>
                Whole day
              </button>
              <button type="button" onClick={() => updateWeeklyTimes(false, weeklyTimeFrom, weeklyTimeTo)}
                className={`px-3 py-1 rounded-lg text-xs border transition-colors ${!weeklyWholeDay ? "bg-amber-500/20 border-amber-500/40 text-amber-300" : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"}`}>
                Time range
              </button>
            </div>
            {!weeklyWholeDay && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-400 text-xs w-8">Start</span>
                  <TimeSelect value={weeklyTimeFrom} onChange={(v) => updateWeeklyTimes(false, v, weeklyTimeTo)} />
                </div>
                <span className="text-slate-500 text-xs">–</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-400 text-xs w-6">End</span>
                  <TimeSelect value={weeklyTimeTo} onChange={(v) => updateWeeklyTimes(false, weeklyTimeFrom, v)} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Date picker mode */}
      <div>
        <Label className="text-slate-300 text-sm mb-2 block">Block specific dates</Label>
        <div className="flex gap-2 mb-3 flex-wrap">
          {["day", "week", "month"].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`px-3 py-1 rounded-lg text-xs font-medium border transition-colors capitalize ${
                mode === m
                  ? "bg-indigo-500/20 border-indigo-500/50 text-indigo-300"
                  : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500"
              }`}
            >
              {m}
            </button>
          ))}
        </div>

        {/* Whole day / Time range toggle */}
        <div className="mb-3 space-y-2">
          <div className="flex gap-2">
            <button type="button" onClick={() => {
              setWholeDay(true);
              // Convert any existing time-range date entries back to whole-day strings
              const updated = periods.map((p) => {
                if (p.date && p.time_from) return p.date;
                return p;
              });
              onChange(updated);
            }}
              className={`px-3 py-1 rounded-lg text-xs border transition-colors ${wholeDay ? "bg-red-500/20 border-red-500/40 text-red-300" : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"}`}>
              Whole day
            </button>
            <button type="button" onClick={() => {
              setWholeDay(false);
              // Convert any existing whole-day date strings/dicts to time-range entries
              const updated = periods.map((p) => {
                if (typeof p === "string") return { date: p, time_from: timeFrom, time_to: timeTo };
                if (p.date && !p.time_from) return { ...p, time_from: timeFrom, time_to: timeTo };
                return p;
              });
              onChange(updated);
            }}
              className={`px-3 py-1 rounded-lg text-xs border transition-colors ${!wholeDay ? "bg-amber-500/20 border-amber-500/40 text-amber-300" : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"}`}>
              Time range
            </button>
          </div>
          {!wholeDay && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-slate-400 text-xs w-8">Start</span>
                <TimeSelect value={timeFrom} onChange={(v) => {
                  setTimeFrom(v);
                  onChange(periods.map((p) => (p.date && p.time_from) ? { ...p, time_from: v } : p));
                }} />
              </div>
              <span className="text-slate-500 text-xs">–</span>
              <div className="flex items-center gap-1.5">
                <span className="text-slate-400 text-xs w-6">End</span>
                <TimeSelect value={timeTo} onChange={(v) => {
                  setTimeTo(v);
                  onChange(periods.map((p) => (p.date && p.time_from) ? { ...p, time_to: v } : p));
                }} />
              </div>
            </div>
          )}
        </div>

        {/* Calendar */}
        <MiniCalendar month={calMonth} onMonthChange={setCalMonth} onDayClick={handleDayClick} mode={mode} periods={periods} />
        <p className="text-xs text-slate-500 mt-1">
          {mode === "day" && `Click a day to ${wholeDay ? "block it (whole day)" : `block ${timeFrom}–${timeTo}`}`}
          {mode === "week" && `Click any day to block the entire week (Mon–Sun)${!wholeDay ? ` · ${timeFrom}–${timeTo}` : ""}`}
          {mode === "month" && `Click any day to block the entire month${!wholeDay ? ` · ${timeFrom}–${timeTo}` : ""}`}
        </p>
      </div>

      {/* List of blocked periods */}
      {periods.filter((p) => !p.weekdays && !p.avail_week).length > 0 && (
        <div className="space-y-1">
          <Label className="text-slate-400 text-xs">Blocked dates</Label>
          {periods.map((p, i) => {
            if (p.weekdays || p.avail_week) return null;
            return (
              <div key={i} className="flex items-center justify-between bg-slate-800 rounded-lg px-3 py-1.5">
                <span className="text-xs text-slate-300">{labelFor(p)}</span>
                <button type="button" onClick={() => removePeriod(i)} className="text-slate-500 hover:text-red-400 ml-2">
                  <X size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Weekly availability override */}
      <div className="border-t border-slate-700 pt-4">
        <Label className="text-slate-300 text-sm mb-1 block">Weekly availability override</Label>
        <p className="text-xs text-slate-500 mb-3">
          Select a week and define which hours are available — everything outside these hours will be blocked for that week only.
        </p>
        <AvailWeekCalendar
          month={availCalMonth}
          onMonthChange={setAvailCalMonth}
          selectedWeekStart={availWeekStart}
          onWeekClick={(day) => {
            const ws = format(startOfWeek(day, { weekStartsOn: 1 }), "yyyy-MM-dd");
            if (ws === availWeekStart) { setAvailWeekStart(null); setAvailDaySlots([]); return; }
            setAvailWeekStart(ws);
            const existing = periods.find((p) => p.avail_week === ws);
            setAvailDaySlots(existing ? [...existing.slots] : []);
          }}
        />
        {availWeekStart && (
          <div className="mt-3 space-y-2">
            <p className="text-xs text-slate-400">
              Week of {format(new Date(availWeekStart + "T00:00:00"), "MMM d")} — select available days & hours:
            </p>
            <div className="space-y-1.5">
              {WEEKDAYS.map((w) => {
                const slot = availDaySlots.find((s) => s.day === w.value);
                return (
                  <div key={w.value} className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => {
                        if (slot) setAvailDaySlots(availDaySlots.filter((s) => s.day !== w.value));
                        else setAvailDaySlots([...availDaySlots, { day: w.value, time_from: "09:00", time_to: "17:00" }].sort((a, b) => a.day - b.day));
                      }}
                      className={`w-10 py-1 rounded-lg text-xs font-medium border transition-colors ${
                        slot ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                          : "bg-slate-800 border-slate-700 text-slate-500 hover:border-slate-600 hover:text-slate-300"
                      }`}
                    >
                      {w.label}
                    </button>
                    {slot && (
                      <>
                        <TimeSelect value={slot.time_from} onChange={(v) =>
                          setAvailDaySlots(availDaySlots.map((s) => s.day === w.value ? { ...s, time_from: v } : s))}
                        />
                        <span className="text-slate-500 text-xs">–</span>
                        <TimeSelect value={slot.time_to} onChange={(v) =>
                          setAvailDaySlots(availDaySlots.map((s) => s.day === w.value ? { ...s, time_to: v } : s))}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <Button
              type="button"
              size="sm"
              disabled={availDaySlots.length === 0}
              onClick={() => {
                const filtered = periods.filter((p) => p.avail_week !== availWeekStart);
                onChange([...filtered, { avail_week: availWeekStart, slots: availDaySlots }]);
                setAvailWeekStart(null);
                setAvailDaySlots([]);
              }}
              className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 text-xs h-8 mt-1"
            >
              Save week availability
            </Button>
          </div>
        )}
        {periods.some((p) => p.avail_week) && (
          <div className="mt-3 space-y-1">
            <Label className="text-slate-400 text-xs">Configured weeks</Label>
            {periods.map((p, i) => {
              if (!p.avail_week) return null;
              const ws = new Date(p.avail_week + "T00:00:00");
              const we = new Date(p.avail_week + "T00:00:00");
              we.setDate(we.getDate() + 6);
              const dayStr = p.slots
                .map((s) => `${WEEKDAYS.find((w) => w.value === s.day)?.label} ${s.time_from}–${s.time_to}`)
                .join(", ");
              return (
                <div key={i} className="flex items-center justify-between bg-slate-800 rounded-lg px-3 py-1.5">
                  <span className="text-xs text-emerald-300">
                    {format(ws, "MMM d")}–{format(we, "MMM d")}: {dayStr}
                  </span>
                  <button type="button" onClick={() => removePeriod(i)} className="text-slate-500 hover:text-red-400 ml-2">
                    <X size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function StaffPage() {
  const { t } = useTranslation();
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [serviceFilter, setServiceFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [blockedOpen, setBlockedOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // staff object to delete
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyStaff);
  const [blockedPeriods, setBlockedPeriods] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/staff").then((r) => setStaff(r.data)).catch(() => toast.error(t("staff_load_error")));
  }, []);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyStaff); setDialogOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, email: s.email || "", is_active: s.is_active, service_ids: s.service_ids || [] });
    setDialogOpen(true);
  };

  const openBlocked = (s) => {
    setEditing(s);
    setBlockedPeriods(s.blocked_periods || []);
    setBlockedOpen(true);
  };

  const toggleServiceId = (id) => {
    setForm((f) => ({
      ...f,
      service_ids: f.service_ids.includes(id) ? f.service_ids.filter((s) => s !== id) : [...f.service_ids, id],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error(t("staff_name_required")); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/staff/${editing.id}`, form);
        toast.success(t("staff_updated"));
      } else {
        await api.post("/staff", form);
        toast.success(t("staff_created"));
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("staff_save_error"));
    } finally { setSaving(false); }
  };

  const handleSaveBlocked = async () => {
    setSaving(true);
    try {
      await api.put(`/staff/${editing.id}`, { blocked_periods: blockedPeriods });
      toast.success("Availability saved");
      setBlockedOpen(false);
      load();
    } catch {
      toast.error(t("staff_save_error"));
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/staff/${deleteConfirm.id}`);
      toast.success(t("staff_deleted"));
      setDeleteConfirm(null);
      load();
    } catch { toast.error(t("staff_delete_error")); }
  };

  const filteredStaff = serviceFilter === "all"
    ? staff
    : staff.filter((s) => !s.service_ids || s.service_ids.length === 0 || s.service_ids.includes(serviceFilter));

  const blockedCount = (s) => {
    const periods = s.blocked_periods || [];
    const parts = [];
    if (periods.some((p) => p.weekdays)) parts.push("weekly rules");
    if (periods.some((p) => p.avail_week)) parts.push("week availability");
    const dateCount = periods.filter((p) => !p.weekdays && !p.avail_week).length;
    if (dateCount > 0) parts.push(`${dateCount} date(s)`);
    return parts.length > 0 ? parts.join(" + ") : null;
  };

  return (
    <div data-testid="staff-page" className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight font-heading text-white">{t("staff_title")}</h1>
        <div className="flex items-center gap-3">
          {services.length > 0 && (
            <select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="bg-slate-900/50 border border-slate-700 text-sm text-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-500"
            >
              <option value="all">All services</option>
              {services.map((svc) => (
                <option key={svc.id} value={svc.id}>{svc.name}</option>
              ))}
            </select>
          )}
          <Button onClick={openCreate} className="bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="add-staff-button">
            <Plus size={16} className="mr-1" /> {t("staff_add")}
          </Button>
        </div>
      </div>

      {staff.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <User size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-lg mb-2">{t("staff_empty")}</p>
          <p className="text-sm">{t("staff_empty_sub")}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredStaff.map((s) => (
            <div key={s.id} className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 hover:border-indigo-500/30 transition-colors group" data-testid={`staff-card-${s.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-9 h-9 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 font-semibold text-sm">
                  {s.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-orange-400" onClick={() => openBlocked(s)} title="Manage availability">
                    <CalendarOff size={14} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-white" onClick={() => openEdit(s)} data-testid={`edit-staff-${s.id}`}>
                    <Pencil size={14} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-400" onClick={() => setDeleteConfirm(s)} data-testid={`delete-staff-${s.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              <h3 className="text-base font-semibold text-white mb-1">{s.name}</h3>
              {s.email && <p className="text-xs text-slate-400">{s.email}</p>}
              <div className="mt-2 flex flex-wrap gap-1">
                {!s.is_active && <Badge className="bg-slate-700/50 text-slate-400 border-slate-600 text-[10px]">{t("inactive")}</Badge>}
                {blockedCount(s) && <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20 text-[10px]">{blockedCount(s)} blocked</Badge>}
                {s.service_ids && s.service_ids.length > 0
                  ? s.service_ids.map((sid) => {
                      const svc = services.find((sv) => sv.id === sid);
                      return svc ? (
                        <Badge key={sid} className="border text-[10px]" style={{ borderColor: svc.color + "60", color: svc.color, backgroundColor: svc.color + "18" }}>
                          {svc.name}
                        </Badge>
                      ) : null;
                    })
                  : <Badge className="bg-slate-800 text-slate-500 border-slate-700 text-[10px]">All services</Badge>
                }
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit staff dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? t("staff_edit") : t("staff_new")}</DialogTitle>
            <DialogDescription className="text-slate-400">{editing ? t("staff_edit_sub") : t("staff_new_sub")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">{t("name")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("staff_name_placeholder")} className="bg-slate-800 border-slate-700" data-testid="staff-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">{t("staff_email_optional")}</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t("staff_email_placeholder")} className="bg-slate-800 border-slate-700" data-testid="staff-email-input" />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-slate-300 text-sm">{t("active")}</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="staff-active-switch" />
            </div>
            {services.length > 0 && (
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Services <span className="text-slate-500 font-normal">(leave empty = all services)</span></Label>
                <div className="flex flex-wrap gap-2">
                  {services.map((svc) => {
                    const selected = form.service_ids.includes(svc.id);
                    return (
                      <button
                        key={svc.id}
                        type="button"
                        onClick={() => toggleServiceId(svc.id)}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                          selected
                            ? "border-indigo-500/60 bg-indigo-500/20 text-indigo-300"
                            : "border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-600"
                        }`}
                      >
                        <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: svc.color || "#6366f1" }} />
                        {svc.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-400">{t("cancel")}</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-500 hover:bg-indigo-600" data-testid="save-staff-button">
              {saving ? t("loading") : t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("staff_delete_confirm_title")}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {t("staff_delete_confirm_sub", { name: deleteConfirm?.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)} className="text-slate-400">{t("cancel")}</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">{t("delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Blocked periods dialog */}
      <Dialog open={blockedOpen} onOpenChange={setBlockedOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Availability — {editing?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">Set days or periods when this staff member is unavailable.</DialogDescription>
          </DialogHeader>
          <div className="py-2 max-h-[60vh] overflow-y-auto pr-1">
            <BlockedPeriodsEditor periods={blockedPeriods} onChange={setBlockedPeriods} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBlockedOpen(false)} className="text-slate-400">{t("cancel")}</Button>
            <Button onClick={handleSaveBlocked} disabled={saving} className="bg-indigo-500 hover:bg-indigo-600">
              {saving ? t("loading") : t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
